// Service for fetching public NOAA/NWS map overlays.
// All endpoints are public, CORS-enabled, and require no API key.

const NWS_ALERTS_BASE_URL = 'https://api.weather.gov/alerts/active';
const USDM_GEOJSON_URL = 'https://mesonet.agron.iastate.edu/geojson/usdm.py';

// Tiny in-memory cache so switching overlays repeatedly doesn't hammer the APIs.
const cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

async function fetchJson(url) {
  const response = await fetch(url, {
    headers: { Accept: 'application/geo+json,application/json' },
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch active NWS alerts as GeoJSON.
 * Optionally filter by state (e.g. 'NE') or a lat,lon point.
 */
export async function fetchNWSAlertsGeoJSON({ area = null, point = null } = {}) {
  const params = new URLSearchParams({ status: 'actual', message_type: 'alert' });
  if (area) params.append('area', area);
  if (point) params.append('point', `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`);

  const url = `${NWS_ALERTS_BASE_URL}?${params.toString()}`;
  const cacheKey = url;

  const cached = getCache(cacheKey);
  if (cached) return cached;

  const data = await fetchJson(url);
  setCache(cacheKey, data);
  return data;
}

/**
 * Fetch the latest U.S. Drought Monitor as GeoJSON.
 */
export async function fetchUSDMGeoJSON() {
  const cached = getCache(USDM_GEOJSON_URL);
  if (cached) return cached;

  const data = await fetchJson(USDM_GEOJSON_URL);
  setCache(USDM_GEOJSON_URL, data);
  return data;
}

/**
 * Clear the in-memory cache (useful for forcing a refresh or testing).
 */
export function clearNOAACache() {
  cache.clear();
}

const noaaService = {
  fetchNWSAlertsGeoJSON,
  fetchUSDMGeoJSON,
  clearNOAACache,
};

export default noaaService;
